<script lang="ts">
	import type { Announcement } from '$lib/server/db/schema';
	import MarkdownRenderer from './MarkdownRenderer.svelte';
	import { fade, scale } from 'svelte/transition';

	let {
		announcement,
		open = false,
		onDismiss,
	} = $props<{
		announcement: Announcement;
		open?: boolean;
		onDismiss: () => void;
	}>();

	function handleBackdropClick() {
		onDismiss();
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			onDismiss();
		}
	}
</script>

<svelte:window onkeydown={handleKeydown} />

{#if open}
	<div
		class="modal-backdrop"
		onclick={handleBackdropClick}
		onkeydown={handleKeydown}
		transition:fade={{ duration: 200 }}
		role="dialog"
		aria-modal="true"
		aria-labelledby="announcement-title"
		tabindex="-1"
	>
		<div
			class="announcement-modal priority-{announcement.priority}"
			transition:scale={{ duration: 300, start: 0.9 }}
		>
			<header class="modal-header">
				<h1 id="announcement-title">{announcement.title}</h1>
				<button
					class="close-button"
					onclick={onDismiss}
					aria-label="Close announcement"
					type="button"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					>
						<line x1="18" y1="6" x2="6" y2="18"></line>
						<line x1="6" y1="6" x2="18" y2="18"></line>
					</svg>
				</button>
			</header>

			<div class="modern-modal-content">
				<MarkdownRenderer
					content={announcement.renderedContent || ''}
					className="announcement-content"
				/>
			</div>

			<footer class="modal-footer">
				<button
					class="dismiss-button priority-{announcement.priority}"
					onclick={onDismiss}
					type="button"
				>
					Got it!
				</button>
			</footer>
		</div>
	</div>
{/if}

<style>
	.modal-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.6);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: var(--lg-z-100);
		padding: var(--lg-space-4);
	}

	.announcement-modal {
		background: var(--lg-bg-primary);
		border-radius: var(--lg-radius-lg);
		box-shadow: var(--lg-shadow-2xl);
		width: 100%;
		max-width: min(48rem, calc(100vw - var(--lg-space-8)));
		max-height: 85vh;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	/* Priority-based styling */
	.announcement-modal.priority-info {
		border-left: 6px solid var(--lg-info);
	}

	.announcement-modal.priority-important {
		border-left: 6px solid var(--lg-warning);
	}

	.announcement-modal.priority-urgent {
		border-left: 6px solid var(--lg-error);
		animation: urgent-pulse var(--lg-transition-slower) infinite;
	}

	@keyframes urgent-pulse {
		0%,
		100% {
			transform: scale(1);
			box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
		}
		50% {
			transform: scale(1.02);
			box-shadow: 0 25px 50px -12px rgba(239, 68, 68, 0.4);
		}
	}

	.modal-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--lg-space-5) var(--lg-space-8) var(--lg-space-4) var(--lg-space-8);
		border-bottom: 1px solid var(--lg-border-light);
		flex-shrink: 0;
	}

	.modal-header h1 {
		font-size: var(--lg-text-2xl);
		font-weight: var(--lg-font-bold);
		color: var(--lg-text-primary);
		margin: 0;
		line-height: var(--lg-leading-tight);
	}

	.close-button {
		background: none;
		border: none;
		cursor: pointer;
		padding: var(--lg-space-2);
		color: var(--lg-text-light);
		border-radius: var(--lg-radius-sm);
		transition: var(--lg-transition-fast);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.close-button:hover {
		color: var(--lg-text-secondary);
		background-color: var(--lg-bg-hover);
	}

	.modern-modal-content {
		flex: 1;
		overflow-y: auto;
		padding: var(--lg-space-5) var(--lg-space-8);
	}

	.modal-footer {
		padding: var(--lg-space-4) var(--lg-space-8) var(--lg-space-5) var(--lg-space-8);
		border-top: 1px solid var(--lg-border-light);
		display: flex;
		justify-content: flex-end;
		flex-shrink: 0;
	}

	.dismiss-button {
		background-color: var(--lg-info);
		color: var(--lg-text-white);
		border: none;
		padding: var(--lg-space-3) var(--lg-space-5);
		border-radius: var(--lg-radius-md);
		font-weight: var(--lg-font-semibold);
		cursor: pointer;
		transition: var(--lg-transition-fast);
		font-size: var(--lg-text-base);
	}

	.dismiss-button:hover {
		background-color: var(--lg-info-hover);
		transform: translateY(-1px);
		box-shadow: var(--lg-shadow-md);
	}

	.dismiss-button.priority-important {
		background-color: var(--lg-warning);
	}

	.dismiss-button.priority-important:hover,
	.dismiss-button.priority-important:focus,
	.dismiss-button.priority-important:active {
		background-color: var(--lg-warning-hover);
		box-shadow: var(--lg-shadow-md);
	}

	.dismiss-button.priority-urgent {
		background-color: var(--lg-error);
	}

	.dismiss-button.priority-urgent:hover,
	.dismiss-button.priority-urgent:focus,
	.dismiss-button.priority-urgent:active {
		background-color: var(--lg-error-hover);
		box-shadow: var(--lg-shadow-md);
	}

	/* Mobile responsiveness - Override modern.css aggressive modal styles */
	@media (max-width: 640px) {
		.modal-backdrop {
			padding: 0.5rem !important;
			position: fixed !important;
			top: 0 !important;
			left: 0 !important;
			width: 100vw !important;
			height: 100vh !important;
			z-index: 1000 !important;
			display: flex !important;
			align-items: center !important;
			justify-content: center !important;
		}

		.announcement-modal {
			max-height: 90vh !important;
			width: calc(100vw - 1rem) !important;
			max-width: calc(100vw - 1rem) !important;
			position: relative !important;
			margin: 0 !important;
			border-radius: 0.75rem !important;
			box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
			background: white !important;
			display: flex !important;
			flex-direction: column !important;
			overflow: hidden !important;
		}

		.modal-header {
			padding: 1rem 1.5rem 0.75rem 1.5rem !important;
			position: relative !important;
			width: auto !important;
			height: auto !important;
		}

		.modal-header h1 {
			font-size: 1.25rem !important;
		}

		.modern-modal-content {
			padding: 1rem 1.5rem !important;
			position: relative !important;
			width: auto !important;
			height: auto !important;
			max-height: none !important;
			flex: 1 !important;
			overflow-y: auto !important;
			overflow-x: hidden !important;
		}

		.modal-footer {
			padding: 0.75rem 1.5rem 1rem 1.5rem !important;
			position: relative !important;
			width: auto !important;
			height: auto !important;
		}

		.dismiss-button {
			padding: 0.625rem 1.25rem !important;
			font-size: 0.925rem !important;
		}
	}

	/* Ensure markdown content has proper spacing */
	.modern-modal-content :global(.announcement-content) {
		max-width: none;
	}

	/* Accessibility improvements */
	.close-button:focus,
	.dismiss-button:focus {
		outline: 2px solid var(--lg-info);
		outline-offset: 2px;
	}

	.announcement-modal.priority-urgent .close-button:focus,
	.announcement-modal.priority-urgent .dismiss-button:focus {
		outline-color: var(--lg-error);
	}

	.announcement-modal.priority-important .close-button:focus,
	.announcement-modal.priority-important .dismiss-button:focus {
		outline-color: var(--lg-warning);
	}
</style>
